import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { toast } from 'react-toastify'
import {
  ArrowsDownUpIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  PlusIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react'
import { GuildModal, GuildFormData } from '../../components/GuildModal/GuildModal'
import { AllGuildTribeHeader, AllGuildTypes, Role } from '../../types/Roles'
import {
  useCreateGuildMutation,
  useLazyGetAllRolesQuery,
  useUpdateGuildMutation,
} from '../../redux/role.endpoints'

type SortColumn = 'role' | 'type'

const GUILD_TYPE_LABELS: Record<string, string> = Object.keys(
  AllGuildTypes,
).reduce((acc, key) => {
  acc[AllGuildTypes[key]] = AllGuildTribeHeader[key]
  return acc
}, {} as Record<string, string>)

const getGuildTypeLabel = (type: string) => GUILD_TYPE_LABELS[type] ?? type

const GuildView = () => {
  const [guilds, setGuilds] = useState<Role[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState<SortColumn>('role')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [modal, setModal] = useState<null | { mode: 'add' | 'edit'; row?: Role }>(
    null,
  )

  const [getGuilds, { data }] = useLazyGetAllRolesQuery()
  const [createGuild] = useCreateGuildMutation()
  const [updateGuild] = useUpdateGuildMutation()

  useEffect(() => {
    getGuilds()
  }, [getGuilds])

  useEffect(() => {
    setGuilds(data || [])
  }, [data])

  const handleSort = (column: SortColumn) => {
    if (column === sortColumn) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const visibleGuilds = useMemo(() => {
    const filtered = guilds.filter(guild =>
      guild.role.toLowerCase().includes(searchValue.toLowerCase()),
    )
    return [...filtered].sort((a, b) => {
      const comparison =
        sortColumn === 'type'
          ? getGuildTypeLabel(a.type).localeCompare(getGuildTypeLabel(b.type))
          : a.role.localeCompare(b.role)
      return sortOrder === 'asc' ? comparison : -comparison
    })
  }, [guilds, searchValue, sortColumn, sortOrder])

  const handleSaveGuild = async (values: GuildFormData) => {
    try {
      if (modal?.mode === 'edit' && modal.row) {
        await updateGuild({
          id: modal.row.id,
          role: values.name,
          type: values.type,
          salary: modal.row.salary,
        }).unwrap()
      } else {
        await createGuild({
          role: values.name,
          type: values.type,
          salary: 0,
        }).unwrap()
      }
      getGuilds()
      setModal(null)
    } catch (error: any) {
      const action = modal?.mode === 'edit' ? 'updating' : 'creating'
      toast.error(
        `An error occurred when ${action} the guild: ${
          error?.data?.error ?? 'Unknown error'
        }`,
        { toastId: 'guild-save-error' },
      )
      return false
    }
  }

  const sortableHeader = (label: string, column: SortColumn, width: string) => (
    <th className="manage-guilds__table-th" style={{ width }}>
      <button
        type="button"
        className="manage-guilds__sort-btn"
        onClick={() => handleSort(column)}
      >
        {label}
        <ArrowsDownUpIcon size={14} weight="regular" color="#717182" />
      </button>
    </th>
  )

  return (
    <div className="container my-4 manage-guilds">
      <Helmet title="Time Off - Guilds" />

      <div className="manage-guilds__header">
        <h1 className="manage-guilds__title">Manage Guilds</h1>
        <button
          type="button"
          className="manage-guilds__add-btn"
          onClick={() => setModal({ mode: 'add' })}
        >
          <PlusIcon size={16} weight="regular" color="#ffffff" />
          Add New Guild
        </button>
      </div>

      <div className="manage-guilds__filters">
        <div className="manage-guilds__search">
          <input
            type="text"
            className="manage-guilds__search-input"
            placeholder="Search by Guild"
            aria-label="Search by Guild"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
          />
          <MagnifyingGlassIcon
            size={16}
            weight="regular"
            className="manage-guilds__search-icon"
          />
        </div>
      </div>

      {visibleGuilds.length === 0 ? (
        <div className="manage-guilds__empty">
          <div className="manage-guilds__empty-icon">
            <UsersThreeIcon size={48} weight="regular" color="#717182" />
          </div>
          <p className="manage-guilds__empty-label">
            No guilds found. Try adjusting your search.
          </p>
        </div>
      ) : (
        <div className="manage-guilds__table-card">
          <table className="manage-guilds__table">
            <thead className="manage-guilds__table-head">
              <tr>
                {sortableHeader('Guild', 'role', '55%')}
                {sortableHeader('Type', 'type', '45%')}
                <th className="manage-guilds__table-th manage-guilds__table-th--actions" />
              </tr>
            </thead>
            <tbody>
              {visibleGuilds.map(guild => (
                <tr
                  key={guild.id}
                  className="manage-guilds__table-row"
                  onClick={() => setModal({ mode: 'edit', row: guild })}
                >
                  <td className="manage-guilds__table-td">{guild.role}</td>
                  <td className="manage-guilds__table-td">
                    {getGuildTypeLabel(guild.type)}
                  </td>
                  <td className="manage-guilds__table-td manage-guilds__table-td--actions">
                    <button
                      type="button"
                      className="manage-guilds__edit-btn"
                      aria-label={`Edit ${guild.role}`}
                      title="Edit"
                      onClick={e => {
                        e.stopPropagation()
                        setModal({ mode: 'edit', row: guild })
                      }}
                    >
                      <PencilSimpleIcon size={18} weight="regular" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <GuildModal
        show={modal !== null}
        mode={modal?.mode ?? 'add'}
        initialValues={
          modal?.row
            ? { name: modal.row.role, type: modal.row.type }
            : undefined
        }
        onClose={() => setModal(null)}
        onSubmit={handleSaveGuild}
      />
    </div>
  )
}

export default GuildView
